import { area as turfArea } from '@turf/area';
import { bbox as turfBbox } from '@turf/bbox';
import { circle as turfCircle } from '@turf/circle';
import { distance as turfDistance } from '@turf/distance';
import { length as turfLength } from '@turf/length';
import type {
  Feature,
  FeatureCollection,
  LineString,
  Point,
  Polygon,
} from 'geojson';

import { GeoJSONGeometry } from '@atom/types/geojson';

import { CURSOR_RADIUS_PX } from './drawingConstants';

export type DrawingMode = 'point' | 'circle' | 'polygon' | 'linestring';
export type Interaction = 'select' | 'grab' | DrawingMode;

// ---------------------------------------------------------------------------
// Grab circle construction
// ---------------------------------------------------------------------------

// 1° of latitude ≈ 111.32 km (earthRadius 6371 km × π / 180). Used to turn the
// equatorial degrees-per-pixel span into a km radius for Turf.
const EARTH_KM_PER_DEGREE = 111.32;

/**
 * Build the 40-px grab circle centred at `latLng` for the current `zoom`:
 * convert the cursor's pixel radius to a km radius and hand it to Turf.
 *
 * Google's Mercator maps the globe (360°) onto 256 × 2^zoom pixels, so one
 * pixel spans `360 / (256 × 2^zoom)` degrees at the equator. We use that
 * equatorial span directly and ignore the latitude cosine factor — so the
 * circle drifts slightly above 40-px away from the equator.
 *
 * Returns a GeoJSON Polygon feature (the same shape circles are committed as).
 */
export const grabCircle = (
  latLng: google.maps.LatLngLiteral,
  zoom: number,
): Feature<Polygon> => {
  const degreesPerPixel = 360 / (256 * Math.pow(2, zoom));
  const radiusDeg = CURSOR_RADIUS_PX * degreesPerPixel;
  const radiusKm = radiusDeg * EARTH_KM_PER_DEGREE;

  return turfCircle([latLng.lng, latLng.lat], radiusKm, {
    units: 'kilometers',
  });
};

// ---------------------------------------------------------------------------
// Region classification
// ---------------------------------------------------------------------------

/**
 * Returns true when the feature represents a region (circle / polygon).
 * Linestrings are measurement-only and do NOT constrain the query bbox.
 */
export const isRegion = (
  feature: Feature | null,
): feature is Feature<Polygon> =>
  feature !== null && feature.geometry?.type === 'Polygon';

/**
 * Derives the query coordinate string from a region feature: the polygon's own
 * outer ring, so results match the exact drawn shape rather than its bounding
 * box. Returns the same JSON string shape that `boundsToCoordinates` produces.
 */
export const regionCoordinates = (feature: Feature<Polygon>): string =>
  JSON.stringify([feature.geometry.coordinates[0]]);

/** Distance of a LineString feature in kilometers (Turf default). */
export const measureDistance = (feature: Feature<LineString>): number =>
  turfLength(feature, { units: 'kilometers' });

/** Area of a Polygon feature in square meters. */
export const measureArea = (feature: Feature<Polygon>): number =>
  turfArea(feature);

/**
 * Radius of a circle Polygon derived from geometry
 */
export const measureRadius = (feature: Feature<Polygon>): number => {
  const [west, south, east, north] = turfBbox(feature);
  const center: [number, number] = [(west + east) / 2, (south + north) / 2];

  // Any boundary vertex — use the first coordinate of the outer ring.
  const [vLng, vLat] = feature.geometry.coordinates[0][0];
  return turfDistance(center, [vLng, vLat], { units: 'kilometers' });
};

// ---------------------------------------------------------------------------
// Overlay → GeoJSON feature construction
//
// Converts the shapes the drawing surface produces (a centre + radius for
// circles, a vertex path for polygons/linestrings) into the GeoJSON features
// the rest of the surface commits and measures.
// ---------------------------------------------------------------------------

export const circleToFeature = (
  center: google.maps.LatLngLiteral,
  radiusMeters: number,
): Feature<Polygon> =>
  turfCircle([center.lng, center.lat], radiusMeters / 1000, {
    units: 'kilometers',
    properties: { mode: 'circle' },
  });

const toPositions = (path: google.maps.LatLngLiteral[]): [number, number][] =>
  path.map(({ lat, lng }) => [lng, lat]);

const samePosition = (one: [number, number], two: [number, number]): boolean =>
  one[0] === two[0] && one[1] === two[1];

export const pathToPolygonFeature = (
  path: google.maps.LatLngLiteral[],
): Feature<Polygon> => {
  const ring = toPositions(path);
  const first = ring.at(0);
  const last = ring.at(-1);
  // A GeoJSON polygon ring must be closed (first position repeated as last);
  // append the first point unless the drawn path already ends on it.
  const closed =
    first && last && !samePosition(first, last) ? [...ring, first] : ring;

  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'Polygon', coordinates: [closed] },
  };
};

export const pathToLineFeature = (
  path: google.maps.LatLngLiteral[],
): Feature<LineString> => ({
  type: 'Feature',
  properties: {},
  geometry: { type: 'LineString', coordinates: toPositions(path) },
});

export const pointToFeature = (
  position: google.maps.LatLngLiteral,
): Feature<Point> => ({
  type: 'Feature',
  properties: {},
  geometry: { type: 'Point', coordinates: [position.lng, position.lat] },
});

export const featureToPoint = (
  feature: Feature<Point>,
): google.maps.LatLngLiteral => ({
  lng: feature.geometry.coordinates[0],
  lat: feature.geometry.coordinates[1],
});

export const toFeature = (geometry: GeoJSONGeometry): Feature => ({
  type: 'Feature',
  properties: {},
  geometry,
});

export const toFeatureCollection = (feature: Feature): FeatureCollection => ({
  type: 'FeatureCollection',
  features: [feature],
});

export const EMPTY_FEATURES: FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};
